import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getBlogPostBySlug } from '../data/blogData';
import { getBlogCategory, getBlogSeoDescription, getBlogSeoTitle } from '../data/blogSeo';
import { faqItems } from '../data/faqData';
import { ROUTES } from '../data/siteLinks';
import seoRoutes from '../data/seoRoutes.json';

type SeoConfig = {
  title: string;
  description: string;
};

const SITE_NAME = 'Ayşegül Saraç';
const SITE_ORIGIN = 'https://www.aysegulsarac.com';
const DEFAULT_IMAGE = `${SITE_ORIGIN}/images/og-image.png`;
const SEO_BY_PATH = seoRoutes as Record<string, SeoConfig>;

const ensureMeta = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });

  return element;
};

const ensureCanonical = (href: string) => {
  let element = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
  return element;
};

const removeCanonical = () => {
  document.head.querySelector('link[rel="canonical"]')?.remove();
};

const ensureJsonLd = (id: string, payload: object | null) => {
  let element = document.head.querySelector(`script[type="application/ld+json"][data-seohead="${id}"]`) as HTMLScriptElement | null;
  if (!payload) {
    if (element) element.remove();
    return;
  }
  if (!element) {
    element = document.createElement('script');
    element.setAttribute('type', 'application/ld+json');
    element.setAttribute('data-seohead', id);
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(payload);
};

const buildBlogPostingJsonLd = (post: ReturnType<typeof getBlogPostBySlug>, canonicalUrl: string) => {
  if (!post) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    headline: post.title,
    description: getBlogSeoDescription(post.slug, post.description),
    image: post.image ? [post.image] : [`${SITE_ORIGIN}/images/og-image.png`],
    datePublished: post.publishedAt,
    dateModified: post.updatedAt,
    author: {
      '@type': 'Person',
      name: post.author || SITE_NAME,
      url: `${SITE_ORIGIN}${ROUTES.about}`,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_ORIGIN}/images/logo@3x.png`,
      },
    },
    inLanguage: 'tr',
    keywords: getBlogCategory(post.slug),
  };
};

const buildFaqJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
});

const SeoHead = () => {
  const location = useLocation();
  const pathname = location.pathname || ROUTES.home;
  const blogPost = pathname.startsWith('/post/') ? getBlogPostBySlug(pathname) : undefined;
  const seo = blogPost
    ? {
        title: getBlogSeoTitle(blogPost.slug, blogPost.title),
        description: getBlogSeoDescription(blogPost.slug, blogPost.description),
      }
    : SEO_BY_PATH[pathname] ?? {
        title: 'Sayfa Bulunamadı | Ayşegül Saraç',
        description: 'Aradığınız sayfa bulunamadı.',
      };
  // Blog post'larında canonicalUrl alanı varsa onu kullan (cannibalization önlemek için
  // bazı post'lar pillar post'a canonical olabilir). Yoksa o anki path'ten oluştur.
  const canonicalUrl = blogPost?.canonicalUrl ?? `${SITE_ORIGIN}${pathname === ROUTES.home ? '' : pathname}`;
  const isIndexable = Boolean(blogPost || SEO_BY_PATH[pathname]);

  useEffect(() => {
    document.title = seo.title;

    ensureMeta('meta[name="description"]', { name: 'description', content: seo.description });
    ensureMeta('meta[property="og:title"]', { property: 'og:title', content: seo.title });
    ensureMeta('meta[property="og:description"]', { property: 'og:description', content: seo.description });
    ensureMeta('meta[property="og:type"]', { property: 'og:type', content: blogPost ? 'article' : 'website' });
    ensureMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    ensureMeta('meta[property="og:image"]', { property: 'og:image', content: blogPost?.image || DEFAULT_IMAGE });
    ensureMeta('meta[property="og:image:width"]', { property: 'og:image:width', content: '1200' });
    ensureMeta('meta[property="og:image:height"]', { property: 'og:image:height', content: '630' });
    ensureMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: 'Ayşegül Saraç — Almanya\'ya Göç ve Yerleşim Rehberi' });
    ensureMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: seo.title });
    ensureMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: seo.description });
    ensureMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: blogPost?.image || DEFAULT_IMAGE });
    if (isIndexable) {
      ensureCanonical(canonicalUrl);
    } else {
      removeCanonical();
    }
    ensureMeta('meta[name="robots"]', {
      name: 'robots',
      content: isIndexable ? 'index, follow' : 'noindex, nofollow',
    });

    // Per-page JSON-LD: blog post için BlogPosting, SSS için FAQPage.
    // Diğer sayfalarda kaldırıyoruz ki ProfessionalService schema'sı (index.html'de
    // hardcoded) tek başına kalsın.
    if (blogPost) {
      ensureJsonLd('blog-posting', buildBlogPostingJsonLd(blogPost, canonicalUrl));
      ensureJsonLd('faq-page', null);
    } else if (pathname === ROUTES.faq) {
      ensureJsonLd('faq-page', buildFaqJsonLd());
      ensureJsonLd('blog-posting', null);
    } else {
      ensureJsonLd('blog-posting', null);
      ensureJsonLd('faq-page', null);
    }
  }, [blogPost, canonicalUrl, isIndexable, location.pathname, location.search, pathname, seo.description, seo.title]);

  return null;
};

export default SeoHead;
